/**
 * Iter 232 (Carlos) — utilidades para inputs numéricos.
 *
 * Cuando el usuario pega "$1.234.567,50" o "1,234,567.50" o "USD 1.234.567" en
 * un input numérico del ERP, queremos guardar `1234567.50` (Number).
 *
 * Reglas:
 * - Quita TODO carácter no numérico excepto el último separador decimal.
 * - El separador decimal detectado es el ÚLTIMO `.` o `,` que tenga 1-2 dígitos
 *   detrás. Si tiene 3 dígitos detrás → se trata como separador de miles.
 * - Soporta `$`, `USD`, `CLP`, espacios, paréntesis (negativo: `(1.234)` → -1234).
 *
 * Uso:
 *   import { parseCurrencyInput, useNumericPaste } from '../lib/numericInput';
 *
 *   <Input
 *     onChange={(e) => setMonto(parseCurrencyInput(e.target.value))}
 *     onPaste={useNumericPaste(setMonto)}
 *   />
 */

/**
 * Convierte un string sucio en un número (o NaN si no se puede).
 * Devuelve string que puede meterse de vuelta en el input (preserva valor sin formato).
 */
export function parseCurrencyInput(raw) {
  if (raw == null) return '';
  let s = String(raw).trim();
  if (!s) return '';

  // Detectar negativo entre paréntesis: (1234) → -1234
  let negative = false;
  if (s.startsWith('(') && s.endsWith(')')) {
    negative = true;
    s = s.slice(1, -1);
  }
  if (s.startsWith('-')) {
    negative = true;
    s = s.slice(1);
  }

  // Quitar todo excepto dígitos, punto y coma
  s = s.replace(/[^0-9.,]/g, '');
  if (!s) return '';

  // Determinar separador decimal: último '.' o ',' cuyo lado derecho tenga 1-2 dígitos.
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  let decimalSepIdx = -1;
  const candidate = Math.max(lastDot, lastComma);
  if (candidate >= 0) {
    const tail = s.slice(candidate + 1);
    if (tail.length >= 1 && tail.length <= 2 && /^\d+$/.test(tail)) {
      decimalSepIdx = candidate;
    }
  }

  let intPart;
  let decPart = '';
  if (decimalSepIdx >= 0) {
    intPart = s.slice(0, decimalSepIdx).replace(/[.,]/g, '');
    decPart = s.slice(decimalSepIdx + 1);
  } else {
    intPart = s.replace(/[.,]/g, '');
  }

  if (!intPart && !decPart) return '';
  const out = `${negative ? '-' : ''}${intPart || '0'}${decPart ? '.' + decPart : ''}`;
  return out;
}

/**
 * Igual que parseCurrencyInput pero devuelve Number (o NaN).
 */
export function parseCurrencyNumber(raw) {
  const s = parseCurrencyInput(raw);
  if (!s) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Handler genérico de paste: previene el default y entrega el valor limpio al setter.
 *
 *   const onPaste = makePasteHandler(setMonto);
 *   <input onPaste={onPaste} />
 *
 * Si el setter espera Number en vez de string, pasar `asNumber: true`.
 */
export function makePasteHandler(setter, { asNumber = false } = {}) {
  return (e) => {
    const text = (e.clipboardData || window.clipboardData)?.getData('text');
    if (text == null) return; // sin clipboardData → dejar default
    const clean = asNumber ? parseCurrencyNumber(text) : parseCurrencyInput(text);
    e.preventDefault();
    setter(clean);
  };
}

/**
 * Hook React: handler de paste reactivo a la prop setter.
 * Wrap conveniente — usarlo si te resulta más cómodo.
 */
export function useNumericPaste(setter, opts) {
  return makePasteHandler(setter, opts);
}
